export const REMOTE_UPDATE = 'yoda/mercure/REMOTE_UPDATE';

type Identified = { _id: string };

export const entityId = (value: AnyObj | string | undefined | null): string => {
    if (value == null)
        return '';
    if (typeof value === 'string')
        return value;
    return String(value._id || '');
};

/** True when the ref is only an id (string or {_id}), not a populated document. */
export const isSparseRef = (value: AnyObj | string | null | undefined): boolean => {
    if (value == null)
        return true;
    if (typeof value === 'string')
        return true;
    if (typeof value !== 'object')
        return true;
    const keys = Object.keys(value).filter(key => key !== '_id' && key !== '__v');
    return keys.length === 0;
};

/**
 * Keep a previously populated ref when Mercure sends only a bare ObjectId
 * for the same entity (avoids wiping client name / assignee / project).
 */
export const preservePopulatedRef = <T extends AnyObj | string | null | undefined>(
    incoming: T,
    previous: T
): T => {
    const incomingId = entityId(incoming);
    const previousId = entityId(previous);
    if (!incomingId || !previousId || incomingId !== previousId)
        return incoming;
    if (isSparseRef(incoming) && !isSparseRef(previous))
        return previous;
    return incoming;
};

const POPULATED_CHANGE_REFS = ['client', 'assignee', 'project'] as const;

/** Merge incoming Mercure change payload with richer local nested refs. */
export const mergePopulatedChangeRefs = (
    incoming: AnyObj,
    previous?: AnyObj | null
): AnyObj => {
    if (!previous || entityId(previous) !== entityId(incoming))
        return incoming;
    const payload = { ...incoming };
    for (const key of POPULATED_CHANGE_REFS) {
        payload[key] = preservePopulatedRef(incoming[key], previous[key]);
    }
    // Quote is a reverse virtual — Mercure change.updated often omits it.
    // Keep the local badge until a quote.* event clears/replaces the link.
    if (isSparseRef(incoming.quote) && !isSparseRef(previous.quote))
        payload.quote = previous.quote;
    // Logs / comments / commits are reverse virtuals — many change.updated
    // payloads omit them. Keep the local activity timeline instead of wiping.
    if (!Array.isArray(incoming.logs) && Array.isArray(previous.logs))
        payload.logs = previous.logs;
    if (!Array.isArray(incoming.comments) && Array.isArray(previous.comments))
        payload.comments = previous.comments;
    if (!Array.isArray(incoming.commits) && Array.isArray(previous.commits))
        payload.commits = previous.commits;
    return payload;
};

export const upsertList = <T extends Identified>(
    list: T[] | undefined,
    data: AnyObj,
    factory: (payload: AnyObj) => T,
    insert = false
): T[] => {
    const items = list || [];
    const next  = factory(data);
    const index = items.findIndex(item => item._id === next._id);
    if (index >= 0)
        return items.map((item, i) => (i === index ? next : item));
    if (insert)
        return [next, ...items];
    return items;
};

export const removeFromList = <T extends Identified>(list: T[] | undefined, id: string): T[] => {
    return (list || []).filter(item => item._id !== id);
};

export const sameAssigneeList = (list: AnyObj[] | undefined, change: AnyObj): boolean => {
    if (!list?.length)
        return false;
    const firstId = entityId(list[0].assignee);
    if (!firstId)
        return false;
    const homogeneous = list.every(item => entityId(item.assignee) === firstId);
    return homogeneous && entityId(change.assignee) === firstId;
};

/** True when a Mercure/API attachment payload matches the loaded list scope. */
export const attachmentMatchesListFilters = (
    data: AnyObj,
    filters: AnyObj | null | undefined
): boolean => {
    if (!filters)
        return false;
    if (filters.project && entityId(data.project) !== String(filters.project))
        return false;
    if (filters.change && entityId(data.change) !== String(filters.change))
        return false;
    return true;
};
